declare const posthog: any;
import { currencyFormatter, rateformatter } from './formatters';
import { getCreditUnionGroups } from './posthog-group'

declare global {
    interface HTMLElement {
        readonly SearchRates: SearchRates | undefined;
    }

}

if (!HTMLElement.prototype.SearchRates) {
    Object.defineProperty(HTMLElement.prototype, 'SearchRates', {
        get: function () {
            const modalId = this.getAttribute('id');
            return SearchRates.instances.get(modalId || '');
        }
    });
}

export default class SearchRates {
    static instances = new Map<string, SearchRates>();

    id: string
    formEl: HTMLFormElement
    termEls: NodeListOf<HTMLInputElement>
    rateEls: NodeListOf<HTMLInputElement>
    cashDownEl: HTMLInputElement | null

    disabledButtonEl: HTMLButtonElement | null
    activeButtonEl: HTMLButtonElement | null
    monthlyPaymentLabelEls: NodeListOf<HTMLElement> | null
    financeAmountLabelEl: HTMLElement | null
    rateLabelEls: NodeListOf<HTMLElement> | null
    errorLabelEl: HTMLElement | null
    rateTitleEl: HTMLElement | null
    rateRequiredEl: HTMLElement | null

    private constructor(triggerElement: HTMLElement) {
        let id = triggerElement.getAttribute('id');
        if (!id) {
            triggerElement.setAttribute('id', 'search-rates-0')
            this.id = 'search-rates-0'
        }
        else {
            this.id = id
        }

        this.formEl = triggerElement as HTMLFormElement;
        this.termEls = this.formEl.querySelectorAll('[search-rates-term]')
        this.rateEls = this.formEl.querySelectorAll('[search-rates-rate]')
        this.cashDownEl = this.formEl.querySelector('[search-rates-cashdown]')

        this.disabledButtonEl = this.formEl.querySelector('[search-rates-disabled-button]')
        this.activeButtonEl = this.formEl.querySelector('[search-rates-active-button]')
        this.monthlyPaymentLabelEls = document.querySelectorAll('[search-rates-monthlypayment-label]')
        this.financeAmountLabelEl = this.formEl.querySelector('[search-rates-financeamount-label]')
        this.rateLabelEls = this.formEl.querySelectorAll('[search-rates-rate-label]')
        this.errorLabelEl = this.formEl.querySelector('[search-rates-error-label]')
        this.rateTitleEl = this.formEl.querySelector('[search-rates-rate-title]')
        this.rateRequiredEl = this.formEl.querySelector('[search-rates-rate-required]')


        this.termEls?.forEach(trigger => {
            trigger.addEventListener('click', () => this.termChanged())
        })

        this.rateEls?.forEach(trigger => {
            trigger.addEventListener('change', () => this.rateChanged())
        })

        this.cashDownEl?.addEventListener('change', () => this.cashDownChanged())

        this.search()
    }

    public async termChanged() {
        if (typeof posthog !== 'undefined') {
            const selectedTerm = Array.from(this.termEls).find(el => el.checked)?.value;
            const vehicleId = this.formEl.querySelector<HTMLInputElement>('input[name="Id"]')?.value ?? undefined;
            if (selectedTerm) {
                posthog.capture('loan_term_changed', {
                    vehicle_id: vehicleId,
                    selected_term: Number(selectedTerm),
                    $groups: getCreditUnionGroups(),
                });
            }
        }

        if (this.hasRate() === false) {
            this.setFinanceRateRequiredState(true)
        }

        this.setRates('')
        await this.search()
    }

    public async rateChanged() {
        this.setFinanceRateRequiredState(this.hasRate() === false && this.isFinanceRateRequiredVisible())
        await this.search()
    }

    public async cashDownChanged() {
        if (this.hasRate() === false) {
            this.setFinanceRateRequiredState(true)
        }

        await this.search()
    }

    public setRates(val: string) {
        this.rateLabelEls?.forEach(item => {
            if (item.tagName == 'INPUT') (item as HTMLInputElement).value = val
            else item.innerHTML = val
        })
    }

    public async search() {
        const formData = new FormData(this.formEl);
        const rateValue = formData.get('Rate')?.toString().replaceAll('%', '').trim() ?? ''
        if (rateValue.length === 0 || Number(rateValue) <= 0) {
            if (this.errorLabelEl) this.errorLabelEl.style.display = 'none'
            if (this.financeAmountLabelEl) this.financeAmountLabelEl.innerHTML = ''
            if (this.monthlyPaymentLabelEls) this.monthlyPaymentLabelEls.forEach(item => item.innerHTML = '')
            this.setRates('')
            if (this.disabledButtonEl) this.disabledButtonEl.style.display = ''
            if (this.activeButtonEl) this.activeButtonEl.style.display = 'none'
            return
        }

        if (this.hasSelectedTerm() === false) {
            if (this.errorLabelEl) this.errorLabelEl.style.display = 'none'
            if (this.financeAmountLabelEl) this.financeAmountLabelEl.innerHTML = ''
            if (this.monthlyPaymentLabelEls) this.monthlyPaymentLabelEls.forEach(item => item.innerHTML = '')
            this.setRates('')
            if (this.disabledButtonEl) this.disabledButtonEl.style.display = ''
            if (this.activeButtonEl) this.activeButtonEl.style.display = 'none'
            return
        }

        if (formData.has('Rate')) {
            formData.set('Rate', rateValue)
        }

        if (formData.get("__RequestVerificationToken") == null) {
            let token = document.querySelector('[name="__RequestVerificationToken"]') as HTMLFormElement
            formData.append("__RequestVerificationToken", token.value)
        }

        const response = await fetch('/details/citystate/vehicle?handler=Search', { method: 'POST', body: formData })
        const data = await response.json()

        if (data.isError == true) {
            if (this.disabledButtonEl) this.disabledButtonEl.style.display = 'none'
            if (this.activeButtonEl) this.activeButtonEl.style.display = ''
            if (this.errorLabelEl) this.errorLabelEl.style.display = ''
            if (this.errorLabelEl) this.errorLabelEl.innerHTML = data.failedReasons

            if (this.financeAmountLabelEl) this.financeAmountLabelEl.innerHTML = ''
            if (this.monthlyPaymentLabelEls) this.monthlyPaymentLabelEls.forEach(item => item.innerHTML = '')
            this.setRates('')
        }
        else {
            this.setFinanceRateRequiredState(false)
            if (this.errorLabelEl) this.errorLabelEl.style.display = 'none'
            if (this.financeAmountLabelEl) this.financeAmountLabelEl.innerHTML = currencyFormatter.format(data.financeAmount)
            if (this.monthlyPaymentLabelEls) this.monthlyPaymentLabelEls.forEach(item => item.innerHTML = currencyFormatter.format(data.monthlyPayment))
            if (this.rateLabelEls) {
                let val = rateformatter.format(data.rate) + ' %'
                this.setRates(val)
            }
            if (this.disabledButtonEl) this.disabledButtonEl.style.display = 'none'
            if (this.activeButtonEl) this.activeButtonEl.style.display = ''

            if (data.failedReasons && this.errorLabelEl) {
                this.errorLabelEl.style.display = ''
                this.errorLabelEl.innerHTML = data.failedReasons.join("<br>");
            }

            //if (data.tradeEquity > 0 || data.tradeEquity < 0) {
            //    $("#trade-equity-section").show()
            //    $("#trade-equity").html(formatter.format(data.tradeEquity))

            //    if (data.tradeEquity < 0) {
            //        $("#trade-equity").css("color", "rgb(255, 68, 88)")
            //    }
            //    else if (data.tradeEquity > 0) {
            //        $("#trade-equity").css("color", "#6dbd44")
            //    }
            //}
            //else {
            //    $("#trade-equity-section").hide()
            //    $("#trade-equity").html("")
            //}

            // Update Product Costs
            //$.each(data.dealershipProducts, function (i, obj) {
            //    $("#ProductCost-" + obj.id).html(formatter.format(obj.monthlyPayment))
            //})

            this.updateTermSliders(data.maxTerm ?? 84)

            if (typeof posthog !== 'undefined') {
                const vehicleId = this.formEl.querySelector<HTMLInputElement>('input[name="Id"]')?.value ?? undefined;
                const selectedTerm = Array.from(this.termEls).find(el => el.checked)?.value ?? undefined;
                const formData2 = new FormData(this.formEl);
                const cashDownRaw = formData2.get('CashDown')?.toString().trim() ?? '';

                posthog.capture('payment_calculator_used', {
                    vehicle_id: vehicleId,
                    term: selectedTerm ? Number(selectedTerm) : undefined,
                    monthly_payment: data.monthlyPayment ?? undefined,
                    finance_amount: data.financeAmount ?? undefined,
                    down_payment: cashDownRaw.length > 0 ? Number(cashDownRaw) : undefined,
                    $groups: getCreditUnionGroups(),
                });
            }
        }
    }

    private hasRate() {
        return this.rateEls.length > 0 && Array.from(this.rateEls).some(item => {
            const rateValue = item.value?.trim() ?? ''
            return rateValue.length > 0 && Number(rateValue) > 0
        })
    }

    private hasSelectedTerm() {
        return Array.from(this.termEls).some(item => item.checked)
    }

    private isFinanceRateRequiredVisible() {
        return this.rateRequiredEl?.classList.contains('hidden') === false
    }

    private setFinanceRateRequiredState(show: boolean) {
        this.rateRequiredEl?.classList.toggle('hidden', !show)
        this.rateTitleEl?.classList.toggle('text-red', show)

        this.rateEls.forEach(item => {
            item.style.outlineColor = show ? '#ff4458' : ''
            item.style.borderColor = show ? '#ff4458' : ''
        })
    }

    private updateTermSliders(maxTerm: number) {
        const termElsArray = Array.from(this.termEls);  // Convert NodeList to Array
        const checkedCheckbox = termElsArray.find(checkbox => checkbox.checked);

        if (checkedCheckbox) {
            let currentTerm = parseInt(checkedCheckbox.value);

            this.toggleTerm(84, maxTerm);
            this.toggleTerm(72, maxTerm);
            this.toggleTerm(60, maxTerm);

            if (currentTerm > maxTerm) {
                let checkboxToCheck = null;

                if (maxTerm >= 36 && maxTerm <= 47) {
                    checkboxToCheck = termElsArray.find(el => el.value === "36");
                } else if (maxTerm >= 48 && maxTerm <= 59) {
                    checkboxToCheck = termElsArray.find(el => el.value === "48");
                } else if (maxTerm >= 60 && maxTerm <= 71) {
                    checkboxToCheck = termElsArray.find(el => el.value === "60");
                } else if (maxTerm >= 72 && maxTerm <= 83) {
                    checkboxToCheck = termElsArray.find(el => el.value === "72");
                } else if (maxTerm >= 84) {
                    checkboxToCheck = termElsArray.find(el => el.value === "84");
                }

                if (checkboxToCheck) {
                    checkboxToCheck.checked = true;
                }
            }
        }
    }

    private toggleTerm(value:number, maxTerm:number) {
        const termElsArray = Array.from(this.termEls);  // Convert NodeList to Array

        const checkbox = termElsArray.find(el => el.value === value.toString()); // Find checkbox by value
        if (checkbox) {
            if (maxTerm < value) {
                checkbox.disabled = true;
                checkbox.closest(".radio-btn-group")?.classList.add("bg-gray-2");
            } else {
                checkbox.disabled = false;
                checkbox.closest(".radio-btn-group")?.classList.remove("bg-gray-2");
            }
        }
    }

    static getInstance(triggerElement: HTMLElement): SearchRates {
        const id = triggerElement.getAttribute('id') ?? '';
        let instance = SearchRates.instances.get(id);

        if (!instance) {
            instance = new SearchRates(triggerElement);
            SearchRates.instances.set(id, instance);
        }

        return instance;
    }
}

// Instantiation based on the HTML element with the `data-modal-trigger` attribute
document.querySelectorAll('[search-rates]').forEach(triggerElement => {
    SearchRates.getInstance(triggerElement as HTMLElement);
});
